<template>
  <div class="level-container">
    <div id="game-container"></div>

    <!-- 游戏状态显示 -->
    <div class="game-stats" v-if="gameStarted && !gameOver">
      <div class="stat-item">
        <span class="stat-icon">🎯</span>
        <span class="stat-value">{{ score }}</span>
        <span class="stat-label">得分</span>
      </div>
      <div class="stat-item">
        <span class="stat-icon">❤️</span>
        <span class="stat-value">{{ lives }}</span>
        <span class="stat-label">生命</span>
      </div>
      <button @click="backToMap" class="exit-btn">
        <span class="button-icon">🚪</span>
        退出游戏
      </button>
    </div>

    <!-- 开始界面 -->
    <div class="game-overlay" v-if="!gameStarted">
      <div class="start-screen">
        <div class="title-section">
          <h2>🌠 陨石大战</h2>
          <p class="subtitle">击落陨石，拯救星球！</p>
          <div class="level-badge">关卡: {{ levelId }}</div>
        </div>

        <div class="game-description">
          <p class="game-intro">
            通过按下屏幕右下方的目标按键，发射子弹击落陨石，每击中一个得10分，达到{{
              currentDifficultySettings
                ? currentDifficultySettings.targetScore
                : 500
            }}分通关。注意不要让陨石落地，否则将损失生命值，共有{{
              currentDifficultySettings ? currentDifficultySettings.lives : 10
            }}条生命。
          </p>

          <!-- 修改当前游戏按键显示 -->
          <div class="key-guide">
            <p class="key-guide-text">当前游戏按键:</p>
            <div class="key-container">
              <div
                v-for="(letter, index) in displayedLetters"
                :key="index"
                class="key"
              >
                {{ letter.toUpperCase() }}
              </div>
              <div
                v-if="letters.length > maxDisplayedKeys"
                class="key more-keys"
                :title="allKeysTooltip"
              >
                ...
              </div>
            </div>
          </div>

          <!-- 添加难度选择 -->
          <div class="difficulty-selection">
            <p class="difficulty-title">选择难度:</p>
            <div class="difficulty-buttons">
              <button
                @click="setDifficulty('easy')"
                class="difficulty-btn"
                :class="{ active: difficulty === 'easy' }"
              >
                <span class="difficulty-icon">🌱</span>
                简单
              </button>
              <button
                @click="setDifficulty('medium')"
                class="difficulty-btn"
                :class="{ active: difficulty === 'medium' }"
              >
                <span class="difficulty-icon">🌿</span>
                中等
              </button>
              <button
                @click="setDifficulty('hard')"
                class="difficulty-btn"
                :class="{ active: difficulty === 'hard' }"
              >
                <span class="difficulty-icon">🔥</span>
                困难
              </button>
            </div>
            <div class="difficulty-description">
              {{ difficultyDescription }}
            </div>
          </div>
        </div>

        <div class="start-buttons">
          <button @click="startGame" class="start-btn pulse-animation">
            <span class="button-icon">🎮</span>
            开始游戏
          </button>
          <button @click="backToStart" class="back-btn">
            <span class="button-icon">🗺️</span>
            返回
          </button>
        </div>
      </div>
    </div>

    <!-- 游戏结束界面 -->
    <div class="game-overlay" v-if="gameOver">
      <div class="game-over">
        <h2>游戏结束</h2>
        <div class="score-display">
          <span class="final-score">{{ score }}</span>
          <span class="score-label">最终得分</span>
        </div>

        <!-- 添加难度和目标分数信息 -->
        <div class="game-result-info">
          <div class="result-item">
            <span class="result-icon" :class="difficultyIconClass">{{
              difficultyIcon
            }}</span>
            <span class="result-label">难度: {{ difficultyName }}</span>
          </div>
          <div class="result-item">
            <span
              class="result-icon"
              :class="{
                success:
                  score >=
                  (currentDifficultySettings
                    ? currentDifficultySettings.targetScore
                    : 500),
              }"
            >
              {{
                score >=
                (currentDifficultySettings
                  ? currentDifficultySettings.targetScore
                  : 500)
                  ? "🏆"
                  : "🎯"
              }}
            </span>
            <span class="result-label">
              目标:
              {{
                currentDifficultySettings
                  ? currentDifficultySettings.targetScore
                  : 500
              }}
              <span
                v-if="
                  score >=
                    (currentDifficultySettings
                      ? currentDifficultySettings.targetScore
                      : 500)
                "
                class="success-text"
                >（已达成）</span
              >
              <span v-else class="fail-text">（未达成）</span>
            </span>
          </div>
        </div>

        <div class="result-buttons">
          <button @click="restartGame" class="game-btn restart-btn">
            <span class="button-icon">🔄</span>
            重新开始
          </button>
          <button @click="backToMap" class="game-btn back-btn">
            <span class="button-icon">🗺️</span>
            返回地图
          </button>
        </div>
      </div>
    </div>

    <!-- 修改字母提示区域 -->
    <div
      class="letter-queue"
      v-if="gameStarted && !gameOver && activeMeteors.length > 0"
    >
      <div class="letter-list">
        <div
          v-for="(meteor, index) in activeMeteors"
          :key="index"
          class="letter-item"
          :class="{ 'first-letter': index === 0 }"
        >
          {{ meteor.letter.toUpperCase() }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Phaser from "phaser";

export default {
  name: "Meteor",
  data() {
    return {
      game: null,
      score: 0,
      lives: 10,
      gameOver: false,
      letters: ["f", "j", "d", "k"], // 默认按键，将被路由参数覆盖
      levelId: "intro9", // 默认关卡ID，将被路由参数覆盖
      currentMeteor: null,
      gameStarted: false,
      bgm: null,
      activeMeteors: [], // 添加活跃陨石数组
      difficulty: "medium", // 默认难度为中等
      maxDisplayedKeys: 4, // 最大显示按键数量
      difficultySettings: {
        easy: {
          meteorSpeed: 30,
          meteorSpawnDelay: 2500,
          lives: 15,
          targetScore: 300,
        },
        medium: {
          meteorSpeed: 50,
          meteorSpawnDelay: 2000,
          lives: 10,
          targetScore: 500,
        },
        hard: {
          meteorSpeed: 80,
          meteorSpawnDelay: 1500,
          lives: 5,
          targetScore: 800,
        },
      },
    };
  },
  computed: {
    difficultyDescription() {
      const descriptions = {
        easy: "陨石下落速度较慢，生命值较多，目标分数较低，适合初学者。",
        medium:
          "陨石下落速度适中，生命值适中，目标分数适中，适合有一定经验的玩家。",
        hard:
          "陨石下落速度较快，生命值较少，目标分数较高，适合挑战高难度的玩家。",
      };
      return descriptions[this.difficulty];
    },
    currentDifficultySettings() {
      return this.difficultySettings[this.difficulty];
    },
    difficultyName() {
      const names = {
        easy: "简单",
        medium: "中等",
        hard: "困难",
      };
      return names[this.difficulty];
    },
    difficultyIcon() {
      const icons = {
        easy: "🌱",
        medium: "🌿",
        hard: "🔥",
      };
      return icons[this.difficulty];
    },
    difficultyIconClass() {
      return {
        "easy-icon": this.difficulty === "easy",
        "medium-icon": this.difficulty === "medium",
        "hard-icon": this.difficulty === "hard",
      };
    },
    // 添加计算属性显示有限数量的按键
    displayedLetters() {
      return this.letters.slice(0, this.maxDisplayedKeys);
    },

    // 添加省略号的工具提示内容
    allKeysTooltip() {
      return `全部按键: ${this.letters
        .map((letter) => letter.toUpperCase())
        .join(", ")}`;
    },
  },
  mounted() {
    // 从路由参数中获取按键和关卡ID
    this.parseRouteParams();

    // 只创建游戏实例，但不启动场景
    this.initGame();
  },
  beforeDestroy() {
    // 调用结束游戏清理所有资源
    this.endGame(false);

    // 销毁游戏实例
    if (this.game) {
      this.game.destroy(true);
      this.game = null;
    }
  },
  methods: {
    // 添加解析路由参数的方法
    parseRouteParams() {
      // 获取路由查询参数
      const { keys, levelId, difficulty } = this.$route.query;

      // 如果存在keys参数，解析并设置按键
      if (keys) {
        // 将字符串转换为字符数组
        this.letters = keys.toLowerCase().split("");
      }

      // 如果存在levelId参数，设置关卡ID
      if (levelId) {
        this.levelId = levelId;
      }

      // 如果存在difficulty参数，设置难度
      if (difficulty && this.difficultySettings[difficulty]) {
        this.difficulty = difficulty;
      }

      console.log("游戏按键设置为:", this.letters);
      console.log("关卡ID设置为:", this.levelId);
      console.log("游戏难度设置为:", this.difficulty);
    },
    initGame() {
      // 如果已经有游戏实例，先销毁它
      if (this.game) {
        this.game.destroy(true);
      }

      // 保存当前组件的引用，以便在场景中使用
      const component = this;

      class MainScene extends Phaser.Scene {
        constructor() {
          super({ key: "MainScene" });
          // 初始化时设置组件引用
          this.component = component;
        }

        init(data) {
          // 确保组件引用在场景初始化时正确设置
          if (data && data.component) {
            this.component = data.component;
          } else if (!this.component) {
            console.warn("场景初始化时未提供组件实例");
            this.component = component; // 使用构造函数中保存的引用作为备份
          }
        }

        preload() {
          this.load.image(
            "meteor",
            "https://wugecdn.steam.fun/typefun/images/starwar/meteor.png"
          );
          this.load.image(
            "bullet",
            "https://wugecdn.steam.fun/typefun/images/starwar/bullet1.png"
          );
          this.load.image(
            "cannon",
            "https://wugecdn.steam.fun/typefun/images/starwar/cannon.png"
          );
          this.load.spritesheet(
            "explosion",
            "https://wugecdn.steam.fun/typefun/images/starwar/explosion.png",
            {
              frameWidth: 64,
              frameHeight: 64,
            }
          );
          this.load.image(
            "star",
            "https://wugecdn.steam.fun/typefun/images/starwar/star.png"
          );
          this.load.audio(
            "shoot",
            "https://wugecdn.steam.fun/typefun/music/shoot.mp3"
          );
          this.load.audio(
            "explosion",
            "https://wugecdn.steam.fun/typefun/music/explosion.mp3"
          );
          this.load.audio(
            "bgm",
            "https://wugecdn.steam.fun/typefun/music/war.mp3"
          );
          this.load.image(
            "fire",
            "https://wugecdn.steam.fun/typefun/images/starwar/fire.png"
          );
          this.load.image(
            "background",
            "https://wugecdn.steam.fun/typefun/images/starwar/space-bg.png"
          ); // 加载背景图
          this.load.image(
            "particle",
            "https://wugecdn.steam.fun/typefun/images/starwar/particle.png"
          ); // 添加粒子图片
        }

        create() {
          // 修改背景图设置
          const bg = this.add.image(0, 0, "background");
          bg.setOrigin(0, 0);
          bg.setDepth(0);

          // 调整背景图大小以完全填充屏幕，包括右侧
          bg.setDisplaySize(this.game.config.width, this.game.config.height);

          // 监听窗口大小变化，确保背景图始终填满屏幕
          this.scale.on("resize", (gameSize) => {
            bg.setDisplaySize(gameSize.width, gameSize.height);
          });

          this.createCannon();
          this.createParticles();
          this.createAnimations();

          this.meteors = this.add.group();
          this.bullets = this.add.group();

          // 根据难度设置陨石生成间隔
          const meteorSpawnDelay = this.component.currentDifficultySettings
            ? this.component.currentDifficultySettings.meteorSpawnDelay
            : 2000; // 默认值

          // 只有在游戏已经开始时才添加陨石生成事件
          if (this.component.gameStarted) {
            this.meteorSpawnEvent = this.time.addEvent({
              delay: meteorSpawnDelay,
              callback: this.spawnMeteor,
              callbackScope: this,
              loop: true,
            });
          }

          // 初始化背景音乐但只有在游戏已经开始时才播放
          this.bgm = this.sound.add("bgm", {
            volume: 0.5,
            loop: true,
          });

          if (this.component.gameStarted) {
            this.bgm.play();
          }
        }

        createCannon() {
          // 调整炮台位置到游戏区中间
          this.cannon = this.add.sprite(
            this.game.config.width / 2, // 水平居中
            this.game.config.height - 100, // 垂直居中，而不是底部
            "cannon"
          );
          this.cannon.setScale(0.1);
          this.cannon.setDepth(1);

          // 添加悬浮动画
          this.tweens.add({
            targets: this.cannon,
            y: "+=20", // 上下浮动20像素
            duration: 1500,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
          });
        }

        createParticles() {
          // 创建粒子管理器
          this.explosionParticles = this.add.particles();
        }

        createAnimations() {
          this.anims.create({
            key: "explode",
            frames: this.anims.generateFrameNumbers("explosion"),
            frameRate: 20,
            hideOnComplete: true,
          });
        }

        spawnMeteor() {
          // 确保组件实例存在
          if (!this.component || !this.component.letters) {
            console.warn("组件实例或字母数组未定义");
            return;
          }

          const x = Phaser.Math.Between(
            this.game.config.width * 0.1,
            this.game.config.width * 0.9
          );
          const letter = this.component.letters[
            Phaser.Math.Between(0, this.component.letters.length - 1)
          ];

          // 创建陨石
          const meteor = this.physics.add.sprite(x, -50, "meteor");
          meteor.letter = letter;
          meteor.setScale(0.1);
          meteor.setDepth(2);

          // 获取陨石的实际显示尺寸
          const meteorSize = meteor.displayWidth;
          const letterSize = meteorSize * 0.5; // 字母大小为陨石的一半

          // 创建字母显示容器
          const letterDisplay = this.add.container(0, 0);
          letterDisplay.setDepth(3);

          // 创建发光背景 - 调整大小
          const glow = this.add.graphics();
          glow.lineStyle(letterSize * 0.2, 0x00ffff, 0.2);
          glow.strokeCircle(0, 0, letterSize * 0.8);

          // 创建主背景 - 调整大小
          const bg = this.add.graphics();
          bg.lineStyle(2, 0x00ffff, 0.8);
          bg.fillStyle(0x000000, 0.7);
          bg.beginPath();
          bg.arc(0, 0, letterSize * 0.7, 0, Math.PI * 2);
          bg.closePath();
          bg.strokePath();
          bg.fillPath();

          // 创建内圈装饰 - 调整大小
          const innerRing = this.add.graphics();
          innerRing.lineStyle(1, 0x00ffff, 0.5);
          innerRing.strokeCircle(0, 0, letterSize * 0.6);

          // 创建字母文本 - 调整大小
          const text = this.add.text(0, 0, letter.toUpperCase(), {
            fontSize: `${letterSize}px`,
            fontFamily: "Arial Black",
            fill: "#00ffff",
            stroke: "#000000",
            strokeThickness: letterSize * 0.1,
            shadow: {
              offsetX: 0,
              offsetY: 0,
              color: "#00ffff",
              blur: letterSize * 0.2,
              fill: true,
            },
          });
          text.setOrigin(0.5);

          // 将所有元素添加到容器中
          letterDisplay.add([glow, bg, innerRing, text]);

          // 添加字母坠落动画效果
          this.tweens.add({
            targets: text,
            y: { from: -5, to: 5 }, // 上下浮动
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
          });

          // 添加字母旋转效果
          this.tweens.add({
            targets: text,
            angle: { from: -3, to: 3 }, // 轻微摇摆
            duration: 1200,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
          });

          // 添加字母缩放呼吸效果
          this.tweens.add({
            targets: text,
            scaleX: { from: 0.95, to: 1.05 },
            scaleY: { from: 0.95, to: 1.05 },
            duration: 1000,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
          });

          // 添加发光效果动画
          this.tweens.add({
            targets: glow,
            alpha: { from: 0.4, to: 0.8 },
            duration: 1500,
            yoyo: true,
            repeat: -1,
            ease: "Sine.easeInOut",
          });

          // 添加背景旋转效果
          this.tweens.add({
            targets: innerRing,
            angle: 360,
            duration: 8000,
            repeat: -1,
            ease: "Linear",
          });

          // 更新字母显示位置 - 调整距离
          const updateLetterPosition = () => {
            letterDisplay.x = meteor.x;
            letterDisplay.y = meteor.y - meteorSize * 0.8; // 调整到更合适的位置
          };

          // 初始更新位置
          updateLetterPosition();

          // 在场景更新时同步位置
          meteor.on("update", updateLetterPosition);

          // 在陨石被销毁时清理字母显示
          meteor.on("destroy", () => {
            const index = this.component.activeMeteors.indexOf(meteor);
            if (index > -1) {
              this.component.activeMeteors.splice(index, 1);
            }
            letterDisplay.destroy();
          });

          // 添加陨石的发光效果
          const meteorGlow = this.add.graphics();
          meteorGlow.setDepth(1);
          meteorGlow.lineStyle(4, 0xff6600, 0.5);
          meteorGlow.strokeCircle(0, 0, meteor.displayWidth * 0.6);

          // 更新发光效果位置
          const updateGlowPosition = () => {
            meteorGlow.x = meteor.x;
            meteorGlow.y = meteor.y;
          };

          // 初始更新发光位置
          updateGlowPosition();

          // 在场景更新时同步发光位置
          meteor.on("update", updateGlowPosition);

          // 在陨石被销毁时清理发光效果
          meteor.on("destroy", () => {
            meteorGlow.destroy();
          });

          // 创建火焰粒子效果，位置在陨石上方一半高度
          const fireParticles = this.add.particles(0, 0, "fire", {
            follow: meteor,
            followOffset: { x: 0, y: -(meteor.displayHeight / 2) }, // 向上偏移陨石高度的一半
            scale: { start: 0.1, end: 0 },
            alpha: { start: 0.8, end: 0 },
            tint: [0xff9900, 0xff0000],
            blendMode: "ADD",
            lifespan: { min: 300, max: 500 },
            quantity: 3,
            frequency: 5,
            angle: { min: 265, max: 275 },
            speed: { min: 50, max: 100 },
            gravityY: -100,
            emitZone: {
              type: "random",
              source: new Phaser.Geom.Circle(0, 0, 3),
              quantity: 3,
            },
          });

          // 调整尾迹效果的位置
          const trailParticles = this.add.particles(0, 0, "fire", {
            follow: meteor,
            followOffset: { x: 0, y: -(meteor.displayHeight / 2) + 3 }, // 稍微低于火焰位置
            scale: { start: 0.05, end: 0 },
            alpha: { start: 0.4, end: 0 },
            tint: [0xff9900, 0xff6600],
            blendMode: "ADD",
            lifespan: { min: 800, max: 1000 },
            frequency: 30,
            quantity: 1,
            gravityY: -50,
            emitZone: {
              type: "random",
              source: new Phaser.Geom.Circle(0, 0, 2),
            },
          });

          // 设置渲染层级
          fireParticles.setDepth(1);
          trailParticles.setDepth(1);

          meteor.on("destroy", () => {
            fireParticles.destroy();
            trailParticles.destroy();
          });

          this.meteors.add(meteor);
          this.component.currentMeteor = meteor;

          // 添加到活跃陨石数组
          this.component.activeMeteors.push(meteor);
        }

        update() {
          this.physics.overlap(
            this.bullets,
            this.meteors,
            this.hitMeteor,
            null,
            this
          );

          this.meteors.getChildren().forEach((meteor) => {
            if (meteor.y > this.game.config.height + meteor.height) {
              this.component.lives--;
              meteor.destroy();

              // 播放失败音效
              this.sound.play("explosion");

              // 创建地面爆炸火焰特效
              const groundFireEmitter = this.add.particles(
                meteor.x,
                this.game.config.height,
                "fire",
                {
                  speed: { min: 200, max: 400 },
                  angle: { min: 250, max: 290 }, // 向上喷射
                  scale: { start: 0.4, end: 0 },
                  lifespan: { min: 600, max: 800 },
                  quantity: 30,
                  frequency: 20,
                  tint: [0xff0000, 0xff6600, 0xffff00],
                  blendMode: "ADD",
                  gravityY: 300, // 添加重力效果
                }
              );

              // 创建冲击波特效
              const shockwaveEmitter = this.add.particles(
                meteor.x,
                this.game.config.height - 10,
                "particle",
                {
                  speed: { min: 100, max: 200 },
                  angle: { min: 180, max: 360 }, // 半圆形扩散
                  scale: { start: 0.2, end: 0 },
                  lifespan: 500,
                  quantity: 20,
                  tint: [0xff9900, 0xff0000],
                  blendMode: "ADD",
                }
              );

              // 创建火星四溅效果
              const sparkEmitter = this.add.particles(
                meteor.x,
                this.game.config.height - 5,
                "particle",
                {
                  speed: { min: 150, max: 300 },
                  angle: { min: 220, max: 320 },
                  scale: { start: 0.1, end: 0 },
                  lifespan: { min: 400, max: 600 },
                  quantity: 15,
                  tint: [0xffff00, 0xff9900],
                  blendMode: "ADD",
                  gravityY: 500,
                }
              );

              // 延迟销毁粒子发射器
              this.time.delayedCall(800, () => {
                groundFireEmitter.destroy();
                shockwaveEmitter.destroy();
                sparkEmitter.destroy();
              });

              if (this.component.lives <= 0) {
                this.component.gameOver = true;
              }
            }
          });
        }

        hitMeteor(bullet, target) {
          // 防止重复处理同一个陨石
          if (target.isDestroyed || bullet.isDestroyed) {
            return;
          }

          // 标记为已销毁
          target.isDestroyed = true;
          bullet.isDestroyed = true;

          // 播放击中音效
          this.sound.play("explosion");

          // 创建主爆炸效果 - 增大范围和数量
          const mainExplosion = this.add.particles(
            target.x,
            target.y,
            "particle",
            {
              speed: { min: 150, max: 300 },
              angle: { min: 0, max: 360 },
              scale: { start: 0.3, end: 0 },
              lifespan: 1000,
              quantity: 30,
              frequency: 30,
              tint: [0xff0000, 0xff6600, 0xffff00],
              blendMode: "ADD",
              rotate: { min: -180, max: 180 },
            }
          );

          // 创建次级爆炸环 - 增大范围
          const ringExplosion = this.add.particles(
            target.x,
            target.y,
            "particle",
            {
              speed: { min: 100, max: 200 },
              angle: { min: 0, max: 360 },
              scale: { start: 0.15, end: 0 },
              lifespan: 800,
              quantity: 20,
              tint: [0x00ffff, 0x1e90ff],
              blendMode: "ADD",
              emitZone: {
                type: "edge",
                source: new Phaser.Geom.Circle(0, 0, 50),
                quantity: 20,
              },
            }
          );

          // 创建发光核心 - 减小范围
          const coreGlow = this.add.particles(target.x, target.y, "particle", {
            speed: { min: 10, max: 30 },
            scale: { start: 0.2, end: 0 },
            lifespan: 300,
            quantity: 3,
            tint: 0xffffff,
            blendMode: "ADD",
            emitZone: {
              type: "random",
              source: new Phaser.Geom.Circle(0, 0, 5),
            },
          });

          // 延迟销毁粒子系统
          this.time.delayedCall(1000, () => {
            mainExplosion.destroy();
            ringExplosion.destroy();
            coreGlow.destroy();
          });

          // 增加得分
          this.component.score += 10;

          // 使用 nextTick 延迟销毁对象
          this.time.delayedCall(10, () => {
            if (bullet.active) {
              bullet.destroy();
            }
            if (target.active) {
              target.destroy();
            }
          });

          // 检查游戏是否胜利
          const targetScore = this.component.currentDifficultySettings
            ? this.component.currentDifficultySettings.targetScore
            : 500; // 默认值

          if (this.component.score >= targetScore) {
            this.component.gameOver = true;
            // 添加保存进度的逻辑
            this.component.completeLevelAndSave();
          }
        }

        shootBullet(target) {
          // 计算炮台到目标的角度
          const angle = Phaser.Math.Angle.Between(
            this.cannon.x,
            this.cannon.y,
            target.x,
            target.y
          );

          // 将弧度转换为角度并调整偏移
          const degrees = Phaser.Math.RadToDeg(angle) - 90;

          // 添加炮台旋转动画
          this.tweens.add({
            targets: this.cannon,
            angle: degrees,
            duration: 100,
            ease: "Power1",
            onComplete: () => {
              // 旋转完成后发射子弹
              const bullet = this.physics.add.sprite(
                this.cannon.x,
                this.cannon.y - 20,
                "bullet"
              );
              bullet.setScale(0.025);
              bullet.setRotation(angle); // 设置子弹的旋转角度
              this.bullets.add(bullet);

              this.sound.play("shoot");

              // 计算子弹飞行时间
              const distance = Phaser.Math.Distance.Between(
                bullet.x,
                bullet.y,
                target.x,
                target.y
              );
              const speed = 800;
              const duration = (distance / speed) * 1000;

              // 使用补间动画直接击中目标
              this.tweens.add({
                targets: bullet,
                x: target.x,
                y: target.y,
                duration: duration,
                ease: "Linear",
                onComplete: () => {
                  this.hitMeteor(bullet, target);
                },
              });

              // 调整子弹尾迹参数
              const bulletTrail = this.add.particles(0, 0, "bullet", {
                follow: bullet,
                scale: { start: 0.05, end: 0 },
                alpha: { start: 0.5, end: 0 },
                speed: 200,
                lifespan: 100,
                blendMode: "ADD",
              });

              bullet.on("destroy", () => {
                bulletTrail.destroy();
              });

              // 炮台回正动画
              this.tweens.add({
                targets: this.cannon,
                angle: 0,
                duration: 200,
                ease: "Power1",
                delay: 100,
              });
            },
          });
        }
      }

      // 获取当前难度设置
      const currentSettings = this.currentDifficultySettings;

      // 修改游戏配置
      this.game = new Phaser.Game({
        type: Phaser.AUTO,
        parent: "game-container",
        width: "100%",
        height: "100%",
        physics: {
          default: "arcade",
          arcade: {
            gravity: { y: currentSettings.meteorSpeed }, // 使用本地变量而不是直接访问计算属性
            debug: false,
          },
        },
        scene: MainScene,
        scale: {
          mode: Phaser.Scale.RESIZE,
          autoCenter: Phaser.Scale.CENTER_BOTH,
          width: window.innerWidth,
          height: window.innerHeight,
        },
        // 添加全局音频配置
        audio: {
          disableWebAudio: false,
          noAudio: false,
        },
      });
    },

    // 添加设置难度的方法
    setDifficulty(difficulty) {
      if (this.difficultySettings[difficulty]) {
        this.difficulty = difficulty;
      }
    },

    startGame() {
      // 移除可能存在的旧事件监听
      window.removeEventListener("keydown", this.handleKeyPress);

      // 重置游戏状态
      this.gameStarted = true;
      this.gameOver = false;
      this.score = 0;
      this.lives = this.currentDifficultySettings.lives; // 根据难度设置生命值
      this.activeMeteors = [];
      this.currentMeteor = null;

      // 确保在启动场景时传递组件实例
      this.game.scene.start("MainScene", { component: this });

      // 添加键盘事件监听
      window.addEventListener("keydown", this.handleKeyPress);

      // 添加窗口失焦事件处理
      window.addEventListener("blur", this.handleWindowBlur);
      window.addEventListener("focus", this.handleWindowFocus);

      // 获取当前场景并启动陨石生成
      setTimeout(() => {
        const scene = this.game.scene.getScene("MainScene");
        if (scene) {
          // 如果场景已经创建但没有陨石生成事件，则添加
          if (!scene.meteorSpawnEvent) {
            const meteorSpawnDelay = this.currentDifficultySettings
              ? this.currentDifficultySettings.meteorSpawnDelay
              : 2000; // 默认值

            scene.meteorSpawnEvent = scene.time.addEvent({
              delay: meteorSpawnDelay,
              callback: scene.spawnMeteor,
              callbackScope: scene,
              loop: true,
            });
          }

          // 播放背景音乐
          if (scene.bgm && !scene.bgm.isPlaying) {
            scene.bgm.play();
          }
        }
      }, 100); // 短暂延迟确保场景已完全加载
    },

    restartGame() {
      // 先调用结束游戏清理所有资源
      this.endGame(false); // 传入false表示不显示游戏结束界面

      // 重置游戏状态
      this.gameStarted = false;
      this.gameOver = false;

      // 短暂延迟后重新开始游戏
      setTimeout(() => {
        // 重新初始化游戏实例
        this.initGame();

        // 然后启动游戏
        setTimeout(() => {
          this.startGame();
        }, 100);
      }, 200);
    },

    completeLevelAndSave() {
      const completedLevels = JSON.parse(
        localStorage.getItem("completedLevels") || "[]"
      );
      if (!completedLevels.includes(this.levelId)) {
        completedLevels.push(this.levelId);
        localStorage.setItem(
          "completedLevels",
          JSON.stringify(completedLevels)
        );
      }
    },

    backToStart() {
      // 停止背景音乐
      const scene = this.game.scene.getScene("MainScene");
      if (scene && scene.bgm) {
        scene.bgm.stop();
      }

      // 返回到打字星球页面
      this.$router.push("/code-classroom/typing-planet");
    },

    backToMap() {
      // 先调用结束游戏清理所有资源
      this.endGame(false);

      // 重置游戏状态
      //this.gameStarted = false
      this.gameOver = false;

      // 短暂延迟后跳转，确保资源清理完毕
      setTimeout(() => {
        this.$router.push("/code-classroom/typing-planet");
      }, 200);
    },

    handleKeyPress(event) {
      if (this.gameOver) return;

      const key = event.key.toLowerCase();
      if (this.letters.includes(key)) {
        // 获取当前场景
        const scene = this.game.scene.getScene("MainScene");
        // 获取所有陨石
        const meteors = scene.meteors.getChildren();
        // 找到第一个匹配的陨石
        const targetMeteor = meteors.find((meteor) => meteor.letter === key);

        if (targetMeteor) {
          scene.shootBullet(targetMeteor);
        }
      }
    },

    endGame(showGameOver = true) {
      // 只有在游戏已经开始的情况下才执行清理
      if (!this.gameStarted) return;

      // 设置游戏结束状态
      if (showGameOver) {
        this.gameOver = true;
      }

      // 停止场景和音乐
      const currentScene = this.game.scene.getScene("MainScene");
      if (currentScene) {
        // 停止音乐
        if (currentScene.bgm) {
          currentScene.bgm.stop();
        }

        // 停止所有计时器事件
        currentScene.time.removeAllEvents();

        // 特别停止陨石生成事件
        if (currentScene.meteorSpawnEvent) {
          currentScene.meteorSpawnEvent.remove();
          currentScene.meteorSpawnEvent = null;
        }

        // 清除所有陨石
        if (currentScene.meteors) {
          currentScene.meteors.clear(true, true);
        }

        // 清除所有子弹
        if (currentScene.bullets) {
          currentScene.bullets.clear(true, true);
        }

        // 停止所有补间动画
        currentScene.tweens.killAll();

        // 停止所有粒子效果
        if (currentScene.explosionParticles) {
          currentScene.explosionParticles.destroy();
        }

        // 暂停场景
        this.game.scene.pause("MainScene");
      }

      // 移除事件监听
      window.removeEventListener("keydown", this.handleKeyPress);
      window.removeEventListener("blur", this.handleWindowBlur);
      window.removeEventListener("focus", this.handleWindowFocus);

      // 重置组件状态
      this.activeMeteors = [];
      this.currentMeteor = null;

      // 检查是否达到通关条件并保存进度
      if (this.score >= 100) {
        this.completeLevelAndSave();
      }
    },

    // 添加窗口失焦处理
    handleWindowBlur() {
      // 游戏未开始或已结束时不处理
      if (!this.gameStarted || this.gameOver) return;

      // 获取当前场景
      const scene = this.game.scene.getScene("MainScene");
      if (scene) {
        // 暂停背景音乐
        if (scene.bgm) {
          scene.bgm.pause();
        }
        // 暂停场景
        this.game.scene.pause("MainScene");
      }
    },

    // 添加窗口获得焦点处理
    handleWindowFocus() {
      // 游戏未开始或已结束时不处理
      if (!this.gameStarted || this.gameOver) return;

      // 获取当前场景
      const scene = this.game.scene.getScene("MainScene");
      if (scene) {
        // 恢复背景音乐
        if (scene.bgm) {
          scene.bgm.resume();
        }
        // 恢复场景
        this.game.scene.resume("MainScene");
      }
    },
  },
};
</script>

<style scoped>
.level-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw; /* 使用视口宽度单位 */
  height: 100vh; /* 使用视口高度单位 */
  background: #000;
  overflow: hidden;
  margin: 0;
  padding: 0;
  z-index: 9999;
}

#game-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%; /* 相对于父容器的100% */
  height: 100%; /* 相对于父容器的100% */
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.game-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
}

.game-over {
  padding: 30px;
  background: rgba(10, 20, 40, 0.9);
  border-radius: 20px;
  box-shadow: 0 0 30px rgba(33, 150, 243, 0.3);
  text-align: center;
  max-width: 450px;
  border: 1px solid rgba(33, 150, 243, 0.3);
  backdrop-filter: blur(12px);
}

.game-btn {
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  min-width: 180px;
  justify-content: center;
}

.restart-btn {
  background: linear-gradient(45deg, #4caf50, #45a049);
  color: white;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.restart-btn:hover {
  background: linear-gradient(45deg, #45a049, #388e3c);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.back-btn {
  background: linear-gradient(45deg, #455a64, #37474f);
  color: white;
  box-shadow: 0 4px 15px rgba(69, 90, 100, 0.3);
}

.back-btn:hover {
  background: linear-gradient(45deg, #37474f, #263238);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(69, 90, 100, 0.4);
}

.button-icon {
  font-size: 24px;
}

.result-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
}

/* 添加按钮点击效果 */
.game-btn:active {
  transform: translateY(1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.start-screen {
  background: rgba(10, 20, 40, 0.85);
  padding: 30px;
  border-radius: 20px;
  max-width: 480px;
  width: 90%;
  color: white;
  box-shadow: 0 0 30px rgba(0, 188, 212, 0.6), 0 0 15px rgba(0, 0, 0, 0.3) inset;
  border: 1px solid rgba(0, 188, 212, 0.4);
  backdrop-filter: blur(12px);
  animation: floatIn 0.5s ease-out;
  transform: scale(0.95);
}

@keyframes floatIn {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(0.95);
  }
}

.title-section {
  text-align: center;
  padding-bottom: 15px;
  margin-bottom: 15px;
  border-bottom: 1px solid rgba(33, 150, 243, 0.3);
}

.title-section h2 {
  color: #fff;
  margin-bottom: 15px;
  font-size: 32px;
  text-shadow: 0 0 15px rgba(0, 255, 255, 0.8);
  letter-spacing: 1px;
  font-weight: bold;
  background: linear-gradient(45deg, #00ffff, #2196f3);
  background-clip: text;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.subtitle {
  font-size: 16px;
  color: #90caf9;
  text-shadow: 0 0 5px rgba(33, 150, 243, 0.2);
  margin-bottom: 15px;
}

.level-badge {
  display: inline-block;
  background: linear-gradient(45deg, #00bcd4, #2196f3);
  color: white;
  padding: 5px 12px;
  border-radius: 15px;
  font-weight: bold;
  font-size: 14px;
  box-shadow: 0 0 10px rgba(33, 150, 243, 0.4);
  margin-top: 5px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.game-description {
  text-align: left;
  margin: 15px 0;
  background: rgba(0, 0, 0, 0.3);
  padding: 18px;
  border-radius: 15px;
  border: 1px solid rgba(33, 150, 243, 0.2);
  font-size: 14px;
  line-height: 1.5;
}

.game-intro {
  margin-bottom: 15px;
  color: #e0e0e0;
}

/* 按键指南区域 */
.key-guide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 15px 0;
  padding: 12px 15px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  border: 1px solid rgba(33, 150, 243, 0.15);
}

.key-guide-text {
  color: #90caf9;
  font-size: 15px;
  text-shadow: 0 0 5px rgba(33, 150, 243, 0.3);
  margin: 0;
}

.key-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
}

.key {
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid #64b5f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: #64b5f6;
  box-shadow: 0 3px 0 #1976d2;
  transition: all 0.2s ease;
}

.key:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 0 #1976d2;
  background: rgba(33, 150, 243, 0.2);
}

.more-keys {
  font-size: 16px;
  color: #90caf9;
  cursor: help;
  animation: ellipsisPulse 2s infinite;
  position: relative;
}

.more-keys:after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  background-color: #90caf9;
  border-radius: 50%;
  opacity: 0.8;
}

/* 难度选择区域 */
.difficulty-selection {
  margin-top: 15px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 12px;
  border: 1px solid rgba(33, 150, 243, 0.15);
}

.difficulty-title {
  color: #90caf9;
  font-size: 15px;
  margin-bottom: 10px;
  text-align: center;
  text-shadow: 0 0 5px rgba(33, 150, 243, 0.3);
  letter-spacing: 1px;
}

.difficulty-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.difficulty-btn {
  padding: 8px 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 90px;
  justify-content: center;
}

.difficulty-description {
  text-align: center;
  color: #e0e0e0;
  font-size: 13px;
  line-height: 1.4;
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  max-width: 95%;
  margin: 0 auto;
}

.start-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
}

.start-btn,
.back-btn {
  padding: 10px 25px;
  font-size: 14px;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.start-btn {
  background: linear-gradient(45deg, #00bcd4, #2196f3);
  color: white;
  box-shadow: 0 4px 12px rgba(0, 188, 212, 0.4);
}

.back-btn {
  background: rgba(0, 0, 0, 0.3);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.pulse-animation {
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
}

/* 游戏状态显示 */
.game-stats {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 15px; /* 减小间距 */
  padding: 10px 15px; /* 减小内边距 */
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px; /* 减小圆角 */
  backdrop-filter: blur(8px);
  box-shadow: 0 0 20px rgba(0, 255, 255, 0.2),
    inset 0 0 10px rgba(0, 255, 255, 0.1);
  border: 1px solid rgba(0, 255, 255, 0.2);
  z-index: 9000; /* 低于overlay但高于游戏画面 */
  transition: all 0.3s ease;
}

.game-stats:hover {
  background: rgba(0, 0, 0, 0.8);
  box-shadow: 0 0 25px rgba(0, 255, 255, 0.3),
    inset 0 0 15px rgba(0, 255, 255, 0.15);
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px; /* 减小间距 */
  color: white;
  padding: 3px 8px; /* 减小内边距 */
  border-radius: 8px; /* 减小圆角 */
  background: rgba(0, 0, 0, 0.3);
  min-width: 70px; /* 减小最小宽度 */
  transition: all 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-3px);
  background: rgba(0, 0, 0, 0.5);
}

.stat-icon {
  font-size: 20px; /* 减小图标大小 */
  filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
}

.stat-value {
  font-size: 24px; /* 减小字体大小 */
  font-weight: bold;
  color: #4caf50;
  text-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
  font-family: "Arial Black", sans-serif;
}

.stat-label {
  font-size: 12px; /* 减小字体大小 */
  opacity: 0.8;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.exit-btn {
  background: linear-gradient(45deg, #455a64, #37474f);
  color: white;
  border: none;
  padding: 8px 15px; /* 减小内边距 */
  border-radius: 8px; /* 减小圆角 */
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px; /* 减小间距 */
  transition: all 0.3s ease;
  font-weight: bold;
  letter-spacing: 1px;
  font-size: 14px; /* 减小字体大小 */
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

.exit-btn .button-icon {
  font-size: 18px; /* 减小图标大小 */
}

.exit-btn:hover {
  background: linear-gradient(45deg, #37474f, #263238);
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.4);
}

.exit-btn:active {
  transform: translateY(1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .guide-section {
    background: rgba(255, 255, 255, 0.05);
  }

  .key {
    background: rgba(255, 255, 255, 0.05);
  }

  .game-stats {
    background: rgba(255, 255, 255, 0.1);
  }

  .stat-value {
    color: #81c784;
  }

  .exit-btn {
    background: #37474f;
  }

  .exit-btn:hover {
    background: #263238;
  }
}

.letter-queue {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: rgba(0, 0, 0, 0.7);
  padding: 15px;
  border-radius: 15px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 255, 255, 0.3);
  z-index: 9000; /* 与game-stats保持同级 */
}

.letter-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.letter-item {
  width: 50px;
  height: 50px;
  background: rgba(0, 255, 255, 0.1);
  border: 2px solid rgba(0, 255, 255, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Arial Black";
  font-size: 24px;
  color: #00ffff;
  text-shadow: 0 0 8px rgba(0, 255, 255, 0.3);
  opacity: 0.7;
  transform: scale(0.9);
  transition: all 0.3s ease;
}

.first-letter {
  width: 60px;
  height: 60px;
  font-size: 32px;
  background: rgba(0, 255, 255, 0.2);
  border: 2px solid rgba(0, 255, 255, 0.8);
  opacity: 1;
  transform: scale(1);
  animation: letterPulse 2s infinite;
  text-shadow: 0 0 12px rgba(0, 255, 255, 0.8);
}

@keyframes letterPulse {
  0% {
    transform: scale(1);
    border-color: rgba(0, 255, 255, 0.8);
  }
  50% {
    transform: scale(1.05);
    border-color: rgba(0, 255, 255, 1);
  }
  100% {
    transform: scale(1);
    border-color: rgba(0, 255, 255, 0.8);
  }
}

/* 添加媒体查询以适应不同屏幕尺寸 */
@media (max-height: 768px) {
  .start-screen {
    padding: 20px;
    max-height: 95%;
  }

  .game-description {
    gap: 10px;
    margin-bottom: 20px;
  }

  .guide-section {
    padding: 15px;
  }

  .start-screen h2 {
    font-size: 28px;
    margin-bottom: 20px;
  }
}

/* 确保在小屏幕上按钮布局合理 */
@media (max-width: 600px) {
  .start-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .start-btn,
  .back-btn {
    width: 100%;
    justify-content: center;
  }
}

@keyframes ellipsisPulse {
  0% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.7;
  }
}

/* 难度按钮的活跃状态 */
.difficulty-btn.active {
  transform: scale(1.05);
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
}

.difficulty-btn:hover {
  background: rgba(0, 0, 0, 0.5);
  transform: translateY(-2px);
}

.difficulty-btn:active {
  transform: translateY(1px);
}

.difficulty-icon {
  font-size: 18px;
}

.difficulty-btn.active[class*="easy"] {
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  border-color: #4caf50;
}

.difficulty-btn.active[class*="medium"] {
  background: linear-gradient(45deg, #2196f3, #03a9f4);
  border-color: #2196f3;
}

.difficulty-btn.active[class*="hard"] {
  background: linear-gradient(45deg, #f44336, #ff5722);
  border-color: #f44336;
}

/* 按钮悬停效果 */
.start-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(0, 188, 212, 0.7);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

/* 游戏结束界面样式 */
.game-over {
  padding: 30px;
  background: rgba(10, 20, 40, 0.9);
  border-radius: 20px;
  box-shadow: 0 0 30px rgba(33, 150, 243, 0.3);
  text-align: center;
  max-width: 450px;
  border: 1px solid rgba(33, 150, 243, 0.3);
  backdrop-filter: blur(12px);
}

.score-display {
  margin: 15px 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.final-score {
  font-size: 50px;
  font-weight: bold;
  color: #4caf50;
  text-shadow: 0 0 15px rgba(76, 175, 80, 0.5);
  font-family: "Arial Black", sans-serif;
}

.score-label {
  font-size: 14px;
  color: #90caf9;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-top: 5px;
}

/* 游戏结果信息样式 */
.game-result-info {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.3);
  padding: 8px 12px;
  border-radius: 10px;
  min-width: 160px;
  border: 1px solid rgba(33, 150, 243, 0.15);
}

.result-icon {
  font-size: 20px;
  filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.5));
}

.easy-icon {
  color: #8bc34a;
  text-shadow: 0 0 10px rgba(139, 195, 74, 0.5);
}

.medium-icon {
  color: #03a9f4;
  text-shadow: 0 0 10px rgba(3, 169, 244, 0.5);
}

.hard-icon {
  color: #ff5722;
  text-shadow: 0 0 10px rgba(255, 87, 34, 0.5);
}

.result-label {
  font-size: 14px;
  color: white;
}

.success-text {
  color: #4caf50;
  font-weight: bold;
  text-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
}

.fail-text {
  color: #f44336;
  font-weight: bold;
  text-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
}

.result-icon.success {
  animation: iconPulse 1.5s infinite;
}

@keyframes iconPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* 媒体查询以适应小屏幕 */
@media (max-width: 480px) {
  .start-screen {
    padding: 20px;
    max-width: 95%;
  }

  .title-section h2 {
    font-size: 26px;
  }

  .difficulty-buttons {
    gap: 8px;
  }

  .difficulty-btn {
    min-width: 80px;
    font-size: 12px;
    padding: 6px 10px;
  }

  .start-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .start-btn,
  .back-btn {
    width: 100%;
  }
}
</style>
